import { Icon } from "@iconify/react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

// Colors matching mobile GlobalStyles
const Color = {
  themeBlue: "#4ea4f5",
  textThemeDark: "#226fb7", // Translation text color
  textPrimary: "#000",
  textHintStrong: "#888",
  borderLight: "#e0e0e0",
  lineDivider: "#eee",
  white: "#fff",
  cancelRed: "#ff4444",
  bgLight: "#f8f9fa",
  gray300: "#d1d5db",
};

// Hide scrollbar CSS
const hideScrollbarStyle = {
  scrollbarWidth: "none" as const,
  msOverflowStyle: "none" as const,
};

// Citation badge for timestamps - gray color
const TimestampBadge = ({ time }: { time: string }) => (
  <span
    className="inline-flex items-center text-[9px] px-1.5 py-0.5 rounded-full ml-1"
    style={{
      backgroundColor: "#f0f0f0",
      color: "#888",
    }}
  >
    {time}
  </span>
);

interface FeatureSectionRecordingProps {
  showDescription?: boolean;
  autoStart?: boolean;
}

const FeatureSectionRecording: React.FC<FeatureSectionRecordingProps> = ({ showDescription = true, autoStart = false }) => {
  const { t } = useTranslation();
  const [streamingText, setStreamingText] = useState("");
  const [translationText, setTranslationText] = useState("");
  const [isRecording, setIsRecording] = useState(false); // Start paused
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioVolume, setAudioVolume] = useState(0);
  const [chatStage, setChatStage] = useState<"input" | "sent">("input");
  const [showAIResponse, setShowAIResponse] = useState(false);
  const [aiStreamingIndex, setAiStreamingIndex] = useState(0);
  const [showBulletPoints, setShowBulletPoints] = useState(autoStart);
  const [bulgingElement, setBulgingElement] = useState<string | null>(null); // Bulge preview before click
  const [cursorVisible, setCursorVisible] = useState(false);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [cursorClicking, setCursorClicking] = useState(false);
  const animationStartedRef = useRef<boolean>(false);
  const bulletPointsShownRef = useRef<boolean>(autoStart); // Track if bullet points have ever been shown
  const transcriptScrollRef = useRef<HTMLDivElement>(null);
  const streamingParagraphRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // Trigger animation as soon as the section is partially visible
  const inViewResult = useInView(cardRef, { amount: 0.3 });
  const isInView = autoStart || inViewResult;

  // AI response parts with timestamp citations only
  const aiResponseParts = [
    { text: t("landing.features.recording.aiResponse.part0"), citation: "00:00" },
    { text: t("landing.features.recording.aiResponse.part1"), citation: "00:10" },
  ];
  const aiResponseFull = aiResponseParts.map(p => p.text).join(" ");
  const aiResponseWords = aiResponseFull.split(" ");

  // First paragraph - Short intro (completed)
  const paragraph1 = {
    timestamp: "00:00",
    english: "Welcome to YouWo AI.",
    chinese: "欢迎使用YouWo AI。",
  };

  // Second paragraph - streams character by character
  const paragraph2 = {
    timestamp: "00:10",
    english: "YouWo is an AI agent platform that turns captured meetings, files, and voice into source-backed outputs. We support real-time transcription in over 60 languages. Record once and let the agent draft the next step.",
    chinese: "YouWo 是一个 AI 智能体平台，可将会议、文件和语音转化为带来源的成果。我们支持60多种语言的实时转录。录制一次，让智能体起草下一步行动。",
  };

  // Timer effect for recording time
  useEffect(() => {
    if (!isRecording) return;
    const timer = setInterval(() => {
      setRecordingTime(prev => prev + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [isRecording]);

  // Audio volume simulation (like mobile RecordingDemo)
  useEffect(() => {
    if (!isRecording) {
      setAudioVolume(0);
      return;
    }
    const volumeInterval = setInterval(() => {
      setAudioVolume(Math.floor(Math.random() * 6));
    }, 150);
    return () => clearInterval(volumeInterval);
  }, [isRecording]);

  // Format time as MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle mic button click - manually trigger full animation loop
  const handleMicClick = async () => {
    // Reset to initial state
    setStreamingText("");
    setTranslationText("");
    setIsRecording(false);
    setRecordingTime(0);
    setChatStage("input");
    setShowAIResponse(false);
    setAiStreamingIndex(0);
    setBulgingElement(null);

    // Reset scroll to top
    if (transcriptScrollRef.current) {
      transcriptScrollRef.current.scrollTo({ top: 0 });
    }

    // Small delay before starting
    await new Promise(r => setTimeout(r, 300));

    // Start recording
    setIsRecording(true);

    // Scroll to bottom to show streaming paragraph
    if (transcriptScrollRef.current) {
      transcriptScrollRef.current.scrollTo({
        top: transcriptScrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    }

    await new Promise(r => setTimeout(r, 800));

    // Stream English text character by character
    for (let i = 0; i <= paragraph2.english.length; i++) {
      setStreamingText(paragraph2.english.slice(0, i));

      const char = paragraph2.english[i];
      let delay = 8 + Math.random() * 10;
      if (char === ' ') delay += 5 + Math.random() * 8;
      if (char === '.' || char === ',') delay += 12 + Math.random() * 12;

      const progress = i / paragraph2.english.length;
      const chineseIndex = Math.floor(progress * paragraph2.chinese.length);
      setTranslationText(paragraph2.chinese.slice(0, chineseIndex));

      await new Promise(r => setTimeout(r, delay));
    }
    setTranslationText(paragraph2.chinese);
    await new Promise(r => setTimeout(r, 500));

    // Stop recording
    setIsRecording(false);
    await new Promise(r => setTimeout(r, 1000));

    // Show AI chat with user message
    setChatStage("sent");
    setShowAIResponse(true);
    await new Promise(r => setTimeout(r, 500));

    // Stream AI response word by word
    for (let i = 0; i <= aiResponseWords.length; i++) {
      setAiStreamingIndex(i);
      await new Promise(r => setTimeout(r, 40));
    }

    // Animation complete - show bullet points
    await new Promise(r => setTimeout(r, 1000));
    setShowBulletPoints(true);
    bulletPointsShownRef.current = true;
  };

  // Animation: Stream text with scroll, then AI chat - CONTINUOUS LOOP (only when in view)
  useEffect(() => {
    // Don't start animation if not in view
    if (!isInView) {
      animationStartedRef.current = false;
      return;
    }

    // Prevent double-run
    if (animationStartedRef.current) return;
    animationStartedRef.current = true;

    let isMounted = true;

    // Helper to wait with abort check
    const wait = (ms: number) => new Promise<boolean>(resolve => {
      const checkAbort = () => {
        if (!isMounted) {
          resolve(false);
          return;
        }
        resolve(true);
      };
      setTimeout(checkAbort, ms);
    });

    const runAnimation = async () => {
      // Don't start if not in view
      if (!isInView) return;

      // Reset card state - start paused
      setStreamingText("");
      setTranslationText("");
      setIsRecording(false); // Start paused
      setRecordingTime(0);
      setChatStage("input");
      setShowAIResponse(false);
      setAiStreamingIndex(0);
      // Only hide bullet points if they haven't been shown yet
      if (!bulletPointsShownRef.current) {
        setShowBulletPoints(false);
      }
      setBulgingElement(null);
      setCursorVisible(false);
      setCursorClicking(false);

      // Reset scroll to top
      if (transcriptScrollRef.current) {
        transcriptScrollRef.current.scrollTo({ top: 0 });
      }

      // Start animation immediately
      if (!await wait(0)) return;

      // STEP 1: Cursor enters and moves to mic button
      setCursorVisible(true);
      setCursorPosition({ x: 400, y: 500 }); // Start from outside bottom-right
      if (!await wait(100)) return;
      // Mic button is at center of left panel control bar - roughly x=170, y=445
      setCursorPosition({ x: 170, y: 445 });
      if (!await wait(400)) return;

      // STEP 2: Bulge on mic button before clicking to start
      setBulgingElement("mic-button");
      if (!await wait(600)) return;

      // Click mic button
      setCursorClicking(true);
      if (!await wait(150)) return;
      setCursorClicking(false);
      setBulgingElement(null);
      setCursorVisible(false);
      setIsRecording(true);

      // STEP 2: Scroll to bottom to show streaming paragraph at top
      if (transcriptScrollRef.current) {
        transcriptScrollRef.current.scrollTo({
          top: transcriptScrollRef.current.scrollHeight,
          behavior: "smooth",
        });
      }

      if (!await wait(800)) return;

      // STEP 3: Stream English text character by character
      for (let i = 0; i <= paragraph2.english.length; i++) {
        if (!isMounted) return;
        setStreamingText(paragraph2.english.slice(0, i));

        // Vary timing for realism
        const char = paragraph2.english[i];
        let delay = 8 + Math.random() * 10;
        if (char === ' ') delay += 5 + Math.random() * 8;
        if (char === '.' || char === ',') delay += 12 + Math.random() * 12;

        // Sync Chinese translation progress
        const progress = i / paragraph2.english.length;
        const chineseIndex = Math.floor(progress * paragraph2.chinese.length);
        setTranslationText(paragraph2.chinese.slice(0, chineseIndex));

        await new Promise(r => setTimeout(r, delay));
      }

      // Complete translation
      setTranslationText(paragraph2.chinese);
      if (!await wait(250)) return;

      // STEP 4: Stop recording
      setIsRecording(false);
      if (!await wait(300)) return;

      // STEP 5: Cursor enters and moves to send button
      setCursorVisible(true);
      setCursorPosition({ x: 400, y: 300 }); // Start from right side
      if (!await wait(100)) return;
      // Send button is in chat panel at bottom-right - roughly x=530, y=450
      setCursorPosition({ x: 530, y: 450 });
      if (!await wait(400)) return;

      // Bulge on send button before sending message
      setBulgingElement("send-button");
      if (!await wait(500)) return;

      // Click send button
      setCursorClicking(true);
      if (!await wait(150)) return;
      setCursorClicking(false);
      setBulgingElement(null);
      setCursorVisible(false);

      // Show AI chat with user message
      setChatStage("sent");
      setShowAIResponse(true);
      if (!await wait(500)) return;

      // STEP 6: Stream AI response word by word
      for (let i = 0; i <= aiResponseWords.length; i++) {
        if (!isMounted || !isInView) return;
        setAiStreamingIndex(i);
        await new Promise(r => setTimeout(r, 40));
      }

      // Animation complete - show bullet points and mark as shown
      if (!await wait(1000)) return;
      setShowBulletPoints(true);
      bulletPointsShownRef.current = true;

      // Wait for reading bullet points (~5 seconds)
      if (!await wait(5000)) return;

      // Wait before restarting loop (8 seconds)
      if (!await wait(8000)) return;
    };

    // Continuous loop
    const animationLoop = async () => {
      while (isMounted) {
        await runAnimation();
        await new Promise(r => setTimeout(r, 500));
      }
    };

    animationLoop();

    return () => {
      isMounted = false;
      animationStartedRef.current = false;
    };
  }, [isInView]);

  return (
    <section className={`${showDescription ? (autoStart ? 'py-4 md:py-8' : 'py-8 md:py-20') : 'py-0'} bg-white overflow-hidden`}>
      <div className={showDescription ? "container mx-auto px-4" : ""}>
        <div className={`${showDescription ? 'grid md:grid-cols-2 gap-10 lg:gap-14 items-start max-w-7xl mx-auto' : 'flex justify-center'}`} style={showDescription ? { minHeight: "auto" } : undefined}>
          {/* Left - Text Content */}
          {showDescription && <motion.div
            initial={{ opacity: 0, x: -50 }}
            {...(autoStart ? { animate: { opacity: 1, x: 0 } } : { whileInView: { opacity: 1, x: 0 }, viewport: { once: true } })}
            transition={{ duration: 0.6 }}
            className="flex flex-col items-start text-left"
          >
            {/* Top group: badge + title + description */}
            <div>
              <div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
                style={{ backgroundColor: `${Color.themeBlue}12` }}
              >
                <Icon icon="lucide:mic" style={{ color: Color.themeBlue }} />
                <span style={{ color: Color.themeBlue }} className="font-medium text-sm">
                  {t("landing.featureDetail.recording.title")}
                </span>
              </div>

              <h2 className="text-3xl md:text-5xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight mt-0">
                {t("landing.features.recording.headline")}
              </h2>

              <p className="text-lg md:text-xl leading-relaxed" style={{ color: "#726F6F" }}>
                {t("landing.featureDetail.recording.description")}
              </p>
            </div>

            {/* Spacer */}
            <div className="flex-1 min-h-[16px] max-h-[32px]" />

            {/* Bottom group: bullets */}
            <div className="block md:hidden space-y-4">
              {[
                { icon: "lucide:zap", text: t("landing.featureDetail.recording.highlight1") },
                { icon: "lucide:globe", text: t("landing.featureDetail.recording.highlight2") },
                { icon: "lucide:languages", text: t("landing.featureDetail.recording.highlight3") },
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${Color.themeBlue}15` }}
                  >
                    <Icon icon={item.icon} className="text-lg" style={{ color: Color.themeBlue }} />
                  </div>
                  <span className="text-base text-gray-700">{item.text}</span>
                </div>
              ))}
            </div>
            <div className="hidden md:block">
              <motion.div
                className="space-y-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: showBulletPoints ? 1 : 0 }}
                transition={{ duration: 0.4 }}
              >
                {[
                  { icon: "lucide:zap", text: t("landing.featureDetail.recording.highlight1") },
                  { icon: "lucide:globe", text: t("landing.featureDetail.recording.highlight2") },
                  { icon: "lucide:languages", text: t("landing.featureDetail.recording.highlight3") },
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    className="flex items-center gap-3"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: showBulletPoints ? 1 : 0, x: showBulletPoints ? 0 : -20 }}
                    transition={{ delay: showBulletPoints ? index * 0.15 : 0, duration: 0.4 }}
                  >
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${Color.themeBlue}15` }}
                    >
                      <Icon icon={item.icon} className="text-lg" style={{ color: Color.themeBlue }} />
                    </div>
                    <span className="text-base text-gray-700">{item.text}</span>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </motion.div>}

          {/* Right - Visual Mockup */}
          <motion.div
            initial={{ opacity: 0, x: showDescription ? 50 : 0 }}
            {...(autoStart ? { animate: { opacity: 1, x: 0 } } : { whileInView: { opacity: 1, x: 0 }, viewport: { once: true } })}
            transition={{ duration: 0.6, delay: showDescription ? 0.2 : 0 }}
            className="px-4 md:px-0"
            style={{ paddingTop: "68px" }}
          >
            <div className="relative w-full max-w-[580px] mx-auto">
              {/* Floating badge - top left: noteLabel */}
              <motion.div
                className="px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1.5 absolute z-10"
                style={{
                  top: -6,
                  left: -6,
                  backgroundColor: Color.themeBlue,
                }}
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Icon icon="lucide:mic" className="text-white text-sm" />
                <span className="text-white text-xs font-medium">
                  {t("landing.features.recording.card.noteLabel")}
                </span>
              </motion.div>
              {/* Floating badge - top right: ask while recording */}
              <motion.div
                className="px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1.5 absolute z-10"
                style={{
                  top: -6,
                  right: -6,
                  backgroundColor: Color.themeBlue,
                }}
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Icon icon="lucide:message-circle" className="text-white text-sm" />
                <span className="text-white text-xs font-medium">
                  {t("landing.featureDetail.recording.askWhileRecording", "Ask while recording")}
                </span>
              </motion.div>
              <div
                ref={cardRef}
                className="rounded-2xl overflow-hidden shadow-2xl"
                style={{
                  backgroundColor: Color.white,
                  border: `1px solid ${Color.borderLight}`,
                  height: 400,
                }}
              >
                {/* Scaled inner content */}
                <div className="origin-top-left" style={{ transform: "scale(0.82)", width: "122%", height: "122%" }}>
                {/* Main Split Content */}
                <div className="flex" style={{ height: 488 }}>
                  {/* Left Panel - Recording/Transcript - percentage width */}
                  <div className="w-[58%] flex flex-col" style={{ borderRight: `1px solid ${Color.borderLight}` }}>
                  {/* Header */}
                  <div
                    className="px-4 py-3 flex items-center justify-end"
                    style={{ borderBottom: `1px solid ${Color.borderLight}` }}
                  >
                    {/* Language indicator */}
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <span>{t("landing.features.recording.card.englishLabel")}</span>
                      <Icon icon="lucide:arrow-right" className="text-[10px]" />
                      <span>中文</span>
                    </div>
                  </div>

                  {/* Transcript Content - Scrollable with auto-scroll */}
                  <div
                    ref={transcriptScrollRef}
                    className="flex-1 p-4 overflow-y-auto"
                    style={{ ...hideScrollbarStyle }}
                  >
                    {/* Part 1 - Complete */}
                    <div className="mb-3" style={{ marginLeft: 15, marginRight: 15 }}>
                      <div
                        className="text-xs font-semibold mb-1"
                        style={{ color: Color.textHintStrong }}
                      >
                        {paragraph1.timestamp}
                      </div>
                      {/* Original text - 16px, textPrimary, regular */}
                      <p
                        className="leading-relaxed"
                        style={{
                          fontSize: 16,
                          color: Color.textPrimary,
                        }}
                      >
                        {paragraph1.english}
                      </p>
                      {/* Translation - 16px, textThemeDark, italic, marginTop 4 */}
                      <p
                        className="leading-relaxed"
                        style={{
                          fontSize: 16,
                          color: Color.textThemeDark,
                          fontStyle: "italic",
                          marginTop: 4,
                        }}
                      >
                        {paragraph1.chinese}
                      </p>
                    </div>

                    {/* Part 2 - Streaming - tall container so text hangs at top when scrolled */}
                    <div ref={streamingParagraphRef} style={{ marginLeft: 15, marginRight: 15, minHeight: 300 }}>
                      <div
                        className="text-xs font-semibold mb-1"
                        style={{ color: Color.textHintStrong }}
                      >
                        {paragraph2.timestamp}
                      </div>
                      {/* Original text - streaming */}
                      <p
                        className="leading-relaxed"
                        style={{
                          fontSize: 16,
                          color: Color.textPrimary,
                        }}
                      >
                        {streamingText}
                        {isRecording && streamingText.length < paragraph2.english.length && (
                          <span
                            className="inline-block w-0.5 h-4 ml-0.5 animate-pulse"
                            style={{ backgroundColor: Color.themeBlue }}
                          />
                        )}
                      </p>
                      {/* Translation - streaming */}
                      {translationText && (
                        <p
                          className="leading-relaxed"
                          style={{
                            fontSize: 16,
                            color: Color.textThemeDark,
                            fontStyle: "italic",
                            marginTop: 4,
                          }}
                        >
                          {translationText}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Recording Bar - Following mobile RecordBar.tsx */}
                  <div style={{ borderTop: `1px solid ${Color.borderLight}` }}>
                    {/* Recording Status - Like mobile topbar */}
                    <div className="h-10 flex items-center justify-center">
                      {isRecording ? (
                        <div className="flex items-center gap-2">
                          {/* Recording animation indicator */}
                          <div className="w-5 h-5 flex items-center justify-center">
                            <div className={`w-3 h-3 rounded-full bg-red-500 ${isRecording ? 'animate-pulse' : ''}`} />
                          </div>
                          {/* Volume bars - 6 bars like mobile (skinny) */}
                          <div className="flex items-center gap-0.5">
                            {[...Array(6)].map((_, i) => (
                              <div
                                key={i}
                                className="w-1 rounded-sm transition-all"
                                style={{
                                  height: audioVolume > i ? 14 : 10,
                                  backgroundColor: audioVolume > i ? Color.themeBlue : Color.gray300,
                                }}
                              />
                            ))}
                          </div>
                          {/* Timer */}
                          <span className="font-mono text-xs font-semibold" style={{ color: Color.textPrimary }}>
                            {formatTime(recordingTime)}
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Icon icon="lucide:square" className="text-gray-400" />
                          <span className="font-mono text-xs" style={{ color: Color.textHintStrong }}>
                            {formatTime(recordingTime)}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Control Buttons - 5 buttons like mobile */}
                    <div className="flex items-center justify-center gap-4 py-2 px-3">
                      {/* 1. Highlight Button - lighter secondary */}
                      <button className="w-7 h-7 rounded-full flex items-center justify-center bg-gray-50">
                        <Icon icon="lucide:highlighter" className="text-xs text-gray-300" />
                      </button>

                      {/* 2. Finish/Stop Button - lighter secondary */}
                      <button
                        className="w-7 h-7 rounded-full flex items-center justify-center border-2"
                        style={{ borderColor: "#f0a0a0", backgroundColor: Color.white }}
                      >
                        <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: "#f0a0a0" }} />
                      </button>

                      {/* 3. Pause/Resume Button (Center - Larger) - MAIN FOCUS - Clickable to start early */}
                      <motion.button
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: Color.themeBlue }}
                        animate={
                          bulgingElement === "mic-button"
                            ? { scale: [1, 1.15, 1.15], boxShadow: ["0 0 0 rgba(78, 164, 245, 0)", "0 0 20px rgba(78, 164, 245, 0.5)", "0 0 20px rgba(78, 164, 245, 0.5)"] }
                            : { scale: 1, boxShadow: "0 0 0 rgba(78, 164, 245, 0)" }
                        }
                        transition={{ duration: bulgingElement === "mic-button" ? 0.5 : 0.2, ease: "easeOut" }}
                        onClick={handleMicClick}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {isRecording ? (
                          <div className="flex gap-1">
                            <div className="w-1 h-4 bg-white rounded-sm" />
                            <div className="w-1 h-4 bg-white rounded-sm" />
                          </div>
                        ) : (
                          <Icon icon="lucide:mic" className="text-white text-lg" />
                        )}
                      </motion.button>

                      {/* 4. Image Picker Button - lighter secondary */}
                      <button className="w-7 h-7 rounded-full flex items-center justify-center bg-gray-50">
                        <Icon icon="lucide:image" className="text-xs text-gray-300" />
                      </button>

                      {/* 5. Camera Button - lighter secondary */}
                      <button className="w-7 h-7 rounded-full flex items-center justify-center bg-gray-50">
                        <Icon icon="lucide:camera" className="text-xs text-gray-300" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Right Panel - AI Chat (white background) - flexible width */}
                <div className="flex-1 flex flex-col" style={{ backgroundColor: Color.white }}>
                  {/* Chat messages area - flex-1 to fill space, top padding for badge clearance */}
                  <div className="flex-1 p-3 pt-6 space-y-2 overflow-y-auto" style={hideScrollbarStyle}>
                    {chatStage === "sent" ? (
                      <>
                        {/* User message - lighter theme blue bg, black text */}
                        <motion.div
                          className="flex "
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                        >
                          <div
                            className="px-3 py-1.5 rounded-2xl text-sm max-w-[85%]"
                            style={{ backgroundColor: `${Color.themeBlue}20`, color: Color.textPrimary }}
                          >
                            {t("landing.features.recording.card.userMessage")}
                          </div>
                        </motion.div>
                        {/* AI response */}
                        {showAIResponse && (
                          <motion.div
                            className="flex gap-2"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                          >
                            <div
                              className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: `${Color.themeBlue}15` }}
                            >
                              <Icon icon="lucide:sparkles" className="text-xs" style={{ color: Color.themeBlue }} />
                            </div>
                            <div className="px-3 py-2 rounded-2xl text-sm leading-relaxed flex-1 text-gray-700" style={{ backgroundColor: Color.white, border: `1px solid ${Color.borderLight}` }}>
                              {aiStreamingIndex > 0 ? (
                                <>
                                  {/* Render text with inline timestamp citations, line break between parts */}
                                  {(() => {
                                    const currentText = aiResponseWords.slice(0, aiStreamingIndex).join(" ");
                                    let result: React.ReactNode[] = [];
                                    let charCount = 0;
                                    aiResponseParts.forEach((part, idx) => {
                                      const partEnd = charCount + part.text.length;
                                      if (currentText.length >= partEnd) {
                                        result.push(
                                          <span key={idx} className="block mb-1.5">
                                            {part.text}
                                            <TimestampBadge time={part.citation} />
                                          </span>
                                        );
                                      } else if (currentText.length > charCount) {
                                        result.push(
                                          <span key={idx}>{currentText.slice(charCount)}</span>
                                        );
                                      }
                                      charCount = partEnd + 1;
                                    });
                                    return result;
                                  })()}
                                  {aiStreamingIndex < aiResponseWords.length && (
                                    <span
                                      className="inline-block w-0.5 h-3 ml-0.5 animate-pulse"
                                      style={{ backgroundColor: Color.themeBlue }}
                                    />
                                  )}
                                </>
                              ) : (
                                <div className="flex items-center gap-1 text-gray-400">
                                  <div className="flex gap-0.5">
                                    <div className="w-1 h-1 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                                    <div className="w-1 h-1 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                                    <div className="w-1 h-1 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                                  </div>
                                </div>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </>
                    ) : (
                      /* Input stage - show placeholder */
                      <div className="h-full flex flex-col items-center justify-center text-center px-4">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center mb-2"
                          style={{ backgroundColor: `${Color.themeBlue}15` }}
                        >
                          <Icon icon="lucide:sparkles" className="text-lg" style={{ color: Color.themeBlue }} />
                        </div>
                        <p className="text-sm text-gray-300">
                          {t("landing.featureDetail.recording.aiEngines", "Ask AI while recording")}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Chat input bar at bottom */}
                  <div className="px-3 py-2 flex-shrink-0" style={{ borderTop: `1px solid ${Color.lineDivider}` }}>
                    <div className="bg-white rounded-xl border border-gray-200 px-3 py-2 flex items-center gap-2">
                      {/* Input placeholder - lighter secondary */}
                      <span className="flex-1 text-sm text-gray-300">
                        {t("landing.features.recording.card.inputPlaceholder")}
                      </span>

                      {/* Send button */}
                      <motion.button
                        className="w-6 h-6 rounded-full border flex items-center justify-center flex-shrink-0 opacity-60"
                        style={{
                          backgroundColor: Color.white,
                          borderColor: Color.borderLight,
                        }}
                        animate={
                          bulgingElement === "send-button"
                            ? { scale: [1, 1.12, 1.12], boxShadow: ["0 0 0 rgba(78, 164, 245, 0)", "0 0 10px rgba(78, 164, 245, 0.22)", "0 0 10px rgba(78, 164, 245, 0.22)"] }
                            : { scale: 1, boxShadow: "0 0 0 rgba(78, 164, 245, 0)" }
                        }
                        transition={{ duration: bulgingElement === "send-button" ? 0.5 : 0.2, ease: "easeOut" }}
                      >
                        <Icon icon="lucide:arrow-up" className="text-xs" style={{ color: Color.themeBlue }} />
                      </motion.button>
                    </div>
                  </div>
                </div>
                </div>{/* close scale wrapper */}
              </div>

              {/* Mouse Cursor - Hidden on mobile */}
              <AnimatePresence>
                {cursorVisible && (
                  <motion.div
                    className="absolute z-50 pointer-events-none hidden md:block"
                    style={{ left: 0, top: 0 }}
                    initial={{ opacity: 0 }}
                    animate={{
                      opacity: 1,
                      x: cursorPosition.x,
                      y: cursorPosition.y,
                      scale: cursorClicking ? 0.8 : 1,
                    }}
                    exit={{ opacity: 0 }}
                    transition={{ type: "spring" as const, stiffness: 200, damping: 25 }}
                  >
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M5.5 3.21V20.8c0 .45.54.67.85.35l4.86-4.86a.5.5 0 0 1 .35-.15h6.87a.5.5 0 0 0 .35-.85L6.35 2.86a.5.5 0 0 0-.85.35Z"
                        fill="#fff"
                        stroke="#000"
                        strokeWidth="1.5"
                      />
                    </svg>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FeatureSectionRecording;
